/**
 * StoryShareCard — sidebar-style "Share this story" card matching
 * socialpulse.blog's article view. Mints a splink.pro-style short URL
 * for the story, renders a QR code, 12 one-tap share buttons, and an
 * "Instagram caption + image" copy helper.
 *
 * Usage
 *   <StoryShareCard
 *     url={window.location.href}
 *     title={event.headline}
 *     summary={event.summary}
 *     hashtag="#NewsPulse"
 *     shortUrlPortal="newspulse"
 *   />
 */
import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Share2, Copy, Check, MessageCircle, Send, Twitter, Facebook,
  Linkedin, MessagesSquare, Mail, MoreHorizontal, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/** Minters a short link once; falls back to the full URL on any error. */
async function mintShortUrl(url, portal) {
  try {
    if (!url || !/^https?:/.test(url)) return url;
    const r = await fetch(`${BACKEND}/api/short-urls`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target_url: url, portal: portal || "newspulse" }),
    });
    if (!r.ok) return url;
    const d = await r.json();
    return d.short_url || url;
  } catch {
    return url;
  }
}

function ShareTile({ label, colorClass, icon: Icon, href, onClick, testId }) {
  const cls = `flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-3 text-white font-bold text-[10px] uppercase tracking-widest transition-opacity hover:opacity-90 ${colorClass}`;
  const inner = (
    <>
      <Icon className="w-5 h-5" strokeWidth={2.2} />
      <span>{label}</span>
    </>
  );
  if (href) return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>
      {inner}
    </a>
  );
  return <button type="button" onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
}

export default function StoryShareCard({
  url,
  title,
  summary = "",
  hashtag = "#NewsPulse",
  shortUrlPortal = "newspulse",
  imageUrl = null,
  className = "",
}) {
  const [short, setShort] = useState(url);
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const enc = encodeURIComponent;

  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await mintShortUrl(url, shortUrlPortal);
      if (alive) setShort(s);
    })();
    return () => { alive = false; };
  }, [url, shortUrlPortal]);

  const shareText = `${title}${hashtag ? " " + hashtag : ""}`;

  const tiles = useMemo(() => ([
    { key: "whatsapp",  label: "WhatsApp",  color: "bg-[#25D366]", icon: MessageCircle,
      href: `https://api.whatsapp.com/send?text=${enc(shareText + " — " + short)}` },
    { key: "telegram",  label: "Telegram",  color: "bg-[#2AABEE]", icon: Send,
      href: `https://t.me/share/url?url=${enc(short)}&text=${enc(shareText)}` },
    { key: "x",         label: "X",         color: "bg-black",     icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(short)}` },
    { key: "facebook",  label: "Facebook",  color: "bg-[#1877F2]", icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(short)}` },
    { key: "linkedin",  label: "LinkedIn",  color: "bg-[#0A66C2]", icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(short)}` },
    { key: "reddit",    label: "Reddit",    color: "bg-[#FF4500]", icon: MessagesSquare,
      href: `https://www.reddit.com/submit?url=${enc(short)}&title=${enc(title)}` },
    { key: "threads",   label: "Threads",   color: "bg-black",     icon: MessagesSquare,
      href: `https://www.threads.net/intent/post?text=${enc(shareText + " " + short)}` },
    { key: "pinterest", label: "Pinterest", color: "bg-[#E60023]", icon: Share2,
      href: `https://pinterest.com/pin/create/button/?url=${enc(short)}&description=${enc(title)}${imageUrl ? "&media=" + enc(imageUrl) : ""}` },
    { key: "email",     label: "Email",     color: "bg-slate-700", icon: Mail,
      href: `mailto:?subject=${enc(title)}&body=${enc((summary ? summary + "\n\n" : "") + short)}` },
    { key: "sms",       label: "SMS",       color: "bg-emerald-600", icon: MessageCircle,
      href: `sms:?body=${enc(shareText + " " + short)}` },
  ]), [short, title, summary, imageUrl, shareText]);

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title, text: summary, url: short }); return; }
      catch { /* dismissed */ }
    }
    copyLink();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(short);
      setCopied(true); toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy"); }
  };

  const copyInstagramCaption = async () => {
    const caption = `${title}\n\n${summary || ""}\n\nRead the full story: ${short}\n\n${hashtag} #Community #LocalNews`;
    try {
      await navigator.clipboard.writeText(caption);
      setCaptionCopied(true); toast.success("Instagram caption copied");
      setTimeout(() => setCaptionCopied(false), 1500);
    } catch { toast.error("Could not copy"); }
  };

  const refreshShort = async () => {
    const s = await mintShortUrl(url, shortUrlPortal);
    setShort(s);
    toast.success("Short link refreshed");
  };

  return (
    <aside className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${className}`}
           data-testid="story-share-card">
      <header className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
          <Share2 className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-900" style={{ fontFamily: "'Lora','Georgia',serif" }}>
            Share this story
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            The News Pulse · splink.pro
          </div>
        </div>
      </header>

      {/* Short URL row */}
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <span className="text-sm text-slate-700 flex-1 truncate font-mono" data-testid="story-share-url">
          {short}
        </span>
        <button type="button" onClick={refreshShort} title="Refresh"
                className="text-slate-400 hover:text-slate-700 p-1" data-testid="story-share-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={copyLink}
                className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-blue-700 px-2 py-1 rounded-md border border-slate-300 bg-white"
                data-testid="story-share-copy">
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* QR */}
      <div className="flex justify-center py-2">
        <div className="p-3 rounded-xl bg-white border border-slate-200" data-testid="story-share-qr">
          <QRCodeSVG value={short} size={168} bgColor="#ffffff" fgColor="#0f172a" level="M" />
        </div>
      </div>

      {/* One-tap grid */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
          One-tap share
        </div>
        <div className="grid grid-cols-4 gap-2">
          {tiles.map((t) => (
            <ShareTile
              key={t.key}
              label={t.label}
              colorClass={t.color}
              icon={t.icon}
              href={t.href}
              testId={`story-share-${t.key}`}
            />
          ))}
          <ShareTile
            label="More"
            colorClass="bg-slate-600"
            icon={MoreHorizontal}
            onClick={nativeShare}
            testId="story-share-more"
          />
        </div>
      </div>

      {/* Instagram caption helper */}
      <button
        type="button"
        onClick={copyInstagramCaption}
        className="w-full flex items-center justify-center gap-2 rounded-full text-white font-semibold text-sm py-3"
        style={{ background: "linear-gradient(90deg,#f9ce34,#ee2a7b,#6228d7)" }}
        data-testid="story-share-instagram"
      >
        {captionCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {captionCopied ? "Instagram caption copied" : "Copy Instagram caption + image"}
      </button>
    </aside>
  );
}
